import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useSearch } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

interface DeviceEvent {
  id: string;
  device_name: string;
  event_name: string;
  properties: Record<string, unknown>;
  client_timestamp: string;
  received_at: string;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function DeviceEvents() {
  const { selectedVenue } = useAuth();
  const { id } = useParams<{ id: string }>();
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);

  const [events, setEvents] = useState<DeviceEvent[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [deviceName, setDeviceName] = useState<string | null>(null);

  const fetchEvents = useCallback(
    async (p: number) => {
      if (!id || !selectedVenue) return;
      setLoading(true);
      try {
        const res = await apiClient["/api/v1/venue/devices/{id}/events"]["get"]({
          parameters: { id, venue_id: selectedVenue.id, page: p, per_page: 25 },
          body: null,
        });
        if (res.code === 200) {
          const body = res.body as { data: DeviceEvent[]; pagination: Pagination };
          setEvents(body.data);
          setPagination(body.pagination);
          if (body.data.length > 0 && !deviceName) {
            setDeviceName(body.data[0].device_name);
          }
        }
      } catch {
        // Ignore
      } finally {
        setLoading(false);
      }
    },
    [id, selectedVenue, deviceName],
  );

  useEffect(() => {
    fetchEvents(page);
  }, [fetchEvents, page]);

  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.per_page))
    : 1;

  const basePath = `/devices/${id}/events`;

  const goToPage = (p: number) => {
    navigate(p === 1 ? basePath : `${basePath}?page=${p}`);
  };

  const title = deviceName ? `Events: ${deviceName}` : "Device Events";

  return (
    <main className="p-6">
      <div className="mb-6">
        <a
          href="/"
          className="text-sm text-text-muted hover:text-accent transition-colors no-underline"
        >
          &larr; Back to Dashboard
        </a>
      </div>

      <h1 className="text-2xl font-bold text-text-primary mb-6">{title}</h1>

      {loading && <p className="text-text-muted text-sm">Loading events...</p>}

      {!loading && events.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No events recorded for this device.</p>
        </div>
      )}

      {!loading && events.length > 0 && (
        <>
          <div className="grid gap-3">
            {events.map((evt) => (
              <div
                key={evt.id}
                className="bg-surface-raised border border-border rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-text-primary font-mono text-sm">
                      {evt.event_name}
                    </div>
                    {evt.properties && Object.keys(evt.properties).length > 0 && (
                      <code className="block mt-2 text-xs text-text-muted font-mono bg-surface rounded p-2 overflow-x-auto whitespace-pre max-h-24 overflow-y-auto">
                        {JSON.stringify(evt.properties, null, 2)}
                      </code>
                    )}
                  </div>
                  <span className="text-sm text-text-muted whitespace-nowrap">
                    {formatTime(evt.client_timestamp)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="px-4 py-2 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-text-muted">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="px-4 py-2 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
